"use client";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadContacts from "@/components/contacts/UploadContacts";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import { CampaignPurpose, PURPOSE_FOCUS_MAP } from "@/constants/onboarding";
import { useAlert } from "@/hooks/useAlert";
import { useOrg } from "@/hooks/useOrg";
import {
  Contact,
  ContactUploadCompletePayload,
} from "@/types/contacts";
import { apiFetch } from "@/lib/api";
import {
  addContactsToCampaignChunked,
  uploadLeadsInChunks,
} from "@/utils/contacts/bulkUpload";
import { createMinDurationEnforcer } from "@/utils/onboarding/common";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { OutboundContactSection } from "./OutboundContactSection";
import { ScriptBuildingLoader } from "./ScriptBuildingLoader";

const CAMPAIGN_SETUP_WORKSTREAM = [
  "initCampaign({ name: config.name })",
  "loadContactList(selectedContacts)",
  "attachScript(campaign, script.id)",
  "configureSequence(campaign.schedule)",
  "setDialingRules({ concurrency: config.max })",
  "mapContactPriority(contacts)",
  "validateCampaignConfig(campaign)",
  "runPreLaunchChecks(campaign)",
  "scheduleFirstBatch(campaign.contacts)",
  "activateCampaign({ status: 'active' })",
  "registerCampaignMetrics(campaign.id)",
  "finalizeLaunch(campaign)",
];

// Phase management — purpose selection is now handled by OutboundScriptStep
type StepPhase = "loading" | "creating" | "contacts" | "error";

interface CampaignSetupStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

export function CampaignSetupStep({ onBack, onNext }: CampaignSetupStepProps) {
  const { t } = useTranslation("onboarding");
  const { t: ct } = useTranslation("contacts");
  const { org, saveOrg } = useOrg();
  const { setAlert, setIsSaving } = useAlert();

  const [phase, setPhase] = useState<StepPhase>("loading");
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const [contactAdded, setContactAdded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const creationRef = useRef(false);

  const existingScriptId = org?.details?.onboarding?.outboundScriptId;
  const existingCampaignId = org?.details?.onboarding?.outboundCampaignId;
  const campaignPurpose =
    (org?.details?.onboarding?.campaignPurpose as CampaignPurpose) ?? "custom";

  // On mount: if campaign already exists skip to contacts, otherwise auto-create
  useEffect(() => {
    if (!org) return;

    if (existingCampaignId) {
      setCampaignId(existingCampaignId);
      setPhase("contacts");
      return;
    }

    // Prevent duplicate creation
    if (creationRef.current) return;
    if (phase !== "loading") return;

    creationRef.current = true;
    createCampaign();
  }, [org, existingCampaignId]);

  // Create campaign automatically using the purpose already saved by OutboundScriptStep
  const createCampaign = useCallback(async () => {
    setPhase("creating");
    setErrorMessage(null);

    const enforceMinDuration = createMinDurationEnforcer(20_000);

    try {
      let newCampaignId: string | undefined;
      let newScriptId: string | undefined = existingScriptId ?? undefined;

      if (existingScriptId) {
        // Script exists (expected) — just create the campaign using it
        const response = await apiFetch<{ success: boolean; campaignId?: string; sequenceId?: string; message?: string }>(
          "/api/onboarding/create-campaign",
          "POST",
          {
          body: {
          scriptId: existingScriptId,
          purpose: campaignPurpose,
        },
        },
        );

        if (!response?.success || !response.campaignId) {
          throw new Error(response?.message ?? "Failed to create campaign");
        }

        newCampaignId = response.campaignId;
      } else {
        // Fallback: no script exists — create everything from scratch
        const focusOptions = PURPOSE_FOCUS_MAP[campaignPurpose];
        const response = await apiFetch<{
            success: boolean;
            campaignId?: string;
            scriptId?: string;
            message?: string;
          }>(
          "/api/onboarding/outbound-scripts",
          "POST",
          {
          body: {
          focusOptions,
        },
        },
        );

        if (!response?.success || !response.campaignId || !response.scriptId) {
          throw new Error(response?.message ?? "Failed to create campaign");
        }

        newCampaignId = response.campaignId;
        newScriptId = response.scriptId;
      }

      // Save campaign ID (and script ID if newly created) to org
      await saveOrg(
        {
          details: {
            ...org?.details,
            onboarding: {
              ...org?.details?.onboarding,
              status: org?.details?.onboarding?.status ?? "in_progress",
              maxCompletedStep: org?.details?.onboarding?.maxCompletedStep ?? 1,
              outboundCampaignId: newCampaignId,
              ...(newScriptId ? { outboundScriptId: newScriptId } : {}),
            },
          },
        },
        { silent: true }
      );

      await enforceMinDuration();
      setCampaignId(newCampaignId);
      setPhase("contacts");
    } catch (error) {
      console.error("Failed to create campaign:", error);
      await enforceMinDuration();
      setErrorMessage(error instanceof Error ? error.message : "Failed to create campaign");
      setPhase("error");
    }
  }, [existingScriptId, campaignPurpose, org, saveOrg]);

  // Handle adding a single phone number
  const handlePhoneSubmit = useCallback(
    async (phoneNumber: string) => {
      if (!campaignId) {
        setAlert({
          status: "error",
          message: t("campaignSetup.noCampaignError"),
          secondsToClose: 4,
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await apiFetch<{ success: boolean }>(
          "/api/onboarding/outbound-contact",
          "POST",
          {
          body: { campaignId, phoneNumber },
        },
        );

        if (!response?.success) {
          throw new Error("Failed to add contact");
        }

        setContactAdded(true);
        setAlert({
          status: "success",
          message: t("campaignSetup.contactAdded"),
          secondsToClose: 4,
        });
      } catch (error) {
        console.error("Failed to add contact:", error);
        setAlert({
          status: "error",
          message: t("campaignSetup.addContactError"),
          secondsToClose: 4,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [campaignId, setAlert, t]
  );

  // Handle upload click
  const handleUploadClick = useCallback(() => {
    setIsUploaderOpen(true);
  }, []);

  // Handle upload completion
  const handleUploadComplete = useCallback(
    async ({ leads }: ContactUploadCompletePayload) => {
      if (!campaignId || leads.length === 0) {
        setIsUploaderOpen(false);
        return;
      }

      setIsUploaderOpen(false);
      setIsSaving(true, ct("uploadingContacts"));

      try {
        // Step 1: Bulk create the contacts (chunked + concurrent; continue
        // on individual batch failure so a single transient network blip
        // doesn't lose every other batch we already uploaded).
        const contactsResponse = await uploadLeadsInChunks(leads);

        if (contactsResponse.succeeded === 0) {
          setAlert({
            status: "error",
            message: ct("fileProcessingError"),
            secondsToClose: 10,
          });
          setIsSaving(false, "");
          return;
        }

        // Filter successful contacts
        const createdContacts = contactsResponse.results.filter(
          (result): result is Contact => "id" in result
        );

        if (createdContacts.length === 0) {
          setAlert({
            status: "error",
            message: ct("fileProcessingError"),
            secondsToClose: 10,
          });
          setIsSaving(false, "");
          return;
        }

        // Step 2: Add all contacts to the campaign (chunked + concurrent).
        // The endpoint returns 204 No Content on success, so we rely on
        // addContactsToCampaignChunked's doNotParse+fallback pattern to
        // distinguish success from HTTP error correctly.
        const { failedRowsUpperBound } = await addContactsToCampaignChunked(
          campaignId,
          createdContacts.map((c) => c.id)
        );

        if (failedRowsUpperBound > 0) {
          setAlert({
            status: "warning",
            message: t("campaignSetup.someContactsFailed", {
              count: failedRowsUpperBound,
            }),
            secondsToClose: 10,
          });
        } else {
          setAlert({
            status: "success",
            message: t("campaignSetup.uploadSuccess", { count: createdContacts.length }),
            secondsToClose: 4,
          });
        }

        setContactAdded(true);
        setIsSaving(false, "");
      } catch (error) {
        console.error("Upload failed:", error);
        setAlert({
          status: "error",
          message: ct("fileProcessingError"),
          secondsToClose: 10,
        });
        setIsSaving(false, "");
      }
    },
    [campaignId, setAlert, setIsSaving, ct, t]
  );

  // Retry after error
  const handleRetry = useCallback(() => {
    creationRef.current = false;
    setPhase("loading");
    setErrorMessage(null);
    // Re-trigger creation via the useEffect by resetting phase
    createCampaign();
  }, [createCampaign]);

  // =========================================================================
  // RENDER: Loading
  // =========================================================================
  if (phase === "loading") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER: Creating campaign
  // =========================================================================
  if (phase === "creating") {
    return (
      <ScriptBuildingLoader
        title={t("campaignSetup.creatingTitle")}
        subtitle={t("campaignSetup.creatingDescription")}
        messages={t("campaignSetup.buildingLoader.messages", { returnObjects: true }) as string[]}
        codeLines={CAMPAIGN_SETUP_WORKSTREAM}
        metricLabels={
          t("campaignSetup.buildingLoader.metrics", { returnObjects: true }) as [
            string,
            string,
            string,
          ]
        }
      />
    );
  }

  // =========================================================================
  // RENDER: Error
  // =========================================================================
  if (phase === "error") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-8">
          <div className="w-full max-w-2xl mx-auto space-y-6">
            <OnboardingStepHeader
              title={t("campaignSetup.title")}
              description={t("campaignSetup.subtitle")}
            />

            <div
              className={clsx(
                "rounded-2xl border border-destructive/50 bg-destructive/5 p-6",
                "text-center space-y-4"
              )}
            >
              <div className="space-y-2">
                <h3 className="font-semibold text-destructive">{t("campaignSetup.error.title")}</h3>
                <p className="text-sm text-muted-foreground">
                  {errorMessage ?? t("campaignSetup.error.generic")}
                </p>
              </div>
              <Button type="primary" onClick={handleRetry}>
                {t("campaignSetup.error.retry")}
              </Button>
            </div>
          </div>
        </div>

        <div
          className={clsx(
            "sticky bottom-0 flex items-center gap-4 px-8 py-4",
            "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
          )}
        >
          {onBack && (
            <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
              {t("wizard.back")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER: Contacts phase
  // =========================================================================
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full max-w-4xl mx-auto space-y-6">
          <OnboardingStepHeader
            title={t("campaignSetup.contactsTitle")}
            description={t("campaignSetup.contactsDescription")}
          />

          {/* Campaign ready badge */}
          <div
            className={clsx(
              "rounded-xl border border-emerald-500/50 bg-emerald-500/5 p-4",
              "flex items-center gap-3"
            )}
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Icon icon="megaphone" className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="font-medium text-emerald-700 dark:text-emerald-300">
                {t("campaignSetup.campaignReady")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("campaignSetup.campaignReadyDescription")}
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <OutboundContactSection
            onPhoneSubmit={handlePhoneSubmit}
            onUploadClick={handleUploadClick}
            isSubmitting={isSubmitting}
            contactAdded={contactAdded}
          />

          {/* Success message */}
          {contactAdded && (
            <div
              className={clsx(
                "rounded-xl border border-emerald-500/50 bg-emerald-500/5 p-4",
                "flex items-center gap-3"
              )}
            >
              <Icon icon="circle-check" className="text-emerald-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-700 dark:text-emerald-300">
                  {t("campaignSetup.contactsReady")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("campaignSetup.contactsReadyDescription")}
                </p>
              </div>
            </div>
          )}

          {/* Info card */}
          <div className="rounded-xl border border-200 bg-panel p-4">
            <div className="flex gap-3">
              <Icon icon="circle-info" className="text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-foreground">{t("campaignSetup.infoTitle")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("campaignSetup.infoDescription")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className={clsx(
          "sticky bottom-0 flex items-center gap-4 px-8 py-4",
          "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
        )}
      >
        {onBack && (
          <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
            {t("wizard.back")}
          </Button>
        )}
        {!contactAdded && (
          <Button type="tertiary" className="min-w-[120px] ml-auto" onClick={onNext}>
            {t("campaignSetup.skipForNow")}
          </Button>
        )}
        <Button
          type="primary"
          className={clsx("min-w-[120px]", contactAdded && "ml-auto")}
          onClick={onNext}
        >
          {t("wizard.next")}
        </Button>
      </div>

      {/* Contact Upload Modal */}
      <UploadContacts
        isUploaderOpen={isUploaderOpen}
        setIsUploaderOpen={setIsUploaderOpen}
        onUploadComplete={handleUploadComplete}
        maxRecordsAllowed={1000}
      />
    </div>
  );
}
